<script setup lang="ts">
import { onBeforeMount, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import { SearchRouteHelper } from "@/helpers";
import { useSearchStore, useUserStore } from "@/stores";
import type { SearchViewType } from "@/types";

const props = defineProps<{
  activeSearchView: SearchViewType;
}>();

const { t } = useI18n();

const searchStore = useSearchStore();
const userStore = useUserStore();

const searchRouteHelper = new SearchRouteHelper(useRouter(), userStore.userData);

const canToggleSearchView = ref(false);

onBeforeMount(async () => {
  const user = await userStore.getUser();
  canToggleSearchView.value = user.featureFlags.showSearchViewToggle;
});

const setPreferredSearchView = async (newPreferredSearchView: SearchViewType) => {
  await userStore.setPreferredSearchView(newPreferredSearchView);

  searchStore.flushResults();
  searchRouteHelper.resetSearchType();
};

interface Button {
  key: SearchViewType;
  title: string;
  ariaLabel: string;
}

const searchViews: Button[] = [
  {
    key: "classic",
    title: t("le-search-view-toggle.classic.title"),
    ariaLabel: t("le-search-view-toggle.classic.aria-label")
  },
  {
    key: "compact",
    title: t("le-search-view-toggle.compact.title"),
    ariaLabel: t("le-search-view-toggle.compact.aria-label")
  }
];
</script>

<template>
  <div v-if="canToggleSearchView" class="le-search-view-toggle">
    <button
      v-for="searchView in searchViews"
      :key="searchView.key"
      class="le-search-view-toggle__button"
      :class="{ 'le-search-view-toggle__button--active': props.activeSearchView === searchView.key }"
      :disabled="props.activeSearchView === searchView.key"
      :aria-label="searchView.ariaLabel"
      @click="setPreferredSearchView(searchView.key)"
    >
      {{ searchView.title }}
    </button>
  </div>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";

.le-search-view-toggle {
  display: flex;
  height: 40px;
  padding: 4px;
  border-radius: 100px;
  background: @colors[@neutrals][100];
  box-shadow: @shadows[button];
  gap: 6px;

  &__button {
    width: 100%;
    border: 0;
    border-radius: 100px;
    background: none;
    .body-text("s", "semi-bold");
    color: @colors[@neutrals][500];

    &--active {
      border: 1px solid @colors[@secondary][500];
      background: @colors[@secondary][300];
    }

    &.focus-visible {
      .focus-outline(green);
    }
  }
}
</style>
